import * as angular from 'angular';
import * as _ from 'lodash';

import * as events from '../../common/events';
import {
    IAuthorizationService,
    IConnectionPointMappingService,
    IConnectionPointTuple,
    ICurrentApplicationService,
    ILoggerService,
    IMailingJobService,
    ISignalrService,
    IStoreService,
    IWideDatabaseUpdateService,
    IstateAuthorizationService
} from '../../services';
import {
    ComponentDefinition,
    ConnectionPoint,
    ConnectionPointTestResult,
    ConnectionPointType,
    ConnectionPointViewModel,
    DateAlignment,
    IApplicationConfiguration,
    TimeUnit
} from '../../models';
import { Helper } from '../../common/helpers';
import { Store, StoreElement } from '../../stateManagement';
import { IRootScope } from '../../IRootScope';
import { MailingJobDefinition } from '../../models/mailing/MailingJobDefinition';

class ConnectionPointsTilesController implements angular.IComponentController {
    static controllerId: string = 'ConnectionPointsTiles';

    private _appConfig: IApplicationConfiguration;
    private _firstLoad: boolean;
    private _mustUpdate: boolean;
    private _saveErrorMessage = 'Save or cancel changes before selecting another connection point.';
    private _unsubscribe: () => void;

    authorizedStates: string[];
    canEdit: boolean;
    connectionPoints: IConnectionPointTuple[] = [];
    formController: angular.IFormController;
    isLoading: boolean;
    isEditMode: boolean;
    isUpdatingDatabase: boolean;
    selectedItem: IConnectionPointTuple;
    state: string;
    status: _.Dictionary<ConnectionPointTestResult>;
    statusLoading: boolean;
    updateDatabasePercent: number;

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$translate',
        '$timeout',
        'storeService',
        'currentApplicationService',
        'loggerService',
        'authorizationService',
        'signalrService',
        'connectionPointMappingService',
        'WideDatabaseUpdateService',
        'stateAuthorizationService',
        'mailingJobService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _$timeout: angular.ITimeoutService,
        private _storeService: IStoreService,
        private _currentApplicationService: ICurrentApplicationService,
        private _loggerService: ILoggerService,
        private _authorizationService: IAuthorizationService,
        private _signalrService: ISignalrService,
        private _connectionPointMappingService: IConnectionPointMappingService,
        private _wideDatabaseUpdateService: IWideDatabaseUpdateService,
        private _stateAuthorizationService: IstateAuthorizationService,
        private _mailingJobService: IMailingJobService) {
    }

    $onInit() {
        this.authorizedStates = this.getAuthorizedStates();
        this.isEditMode = this._$rootScope.isEditMode;
        this.canEdit = this._authorizationService.canEdit();
        this.updateDatabasePercent = 0;
        this.isUpdatingDatabase = false;
        this.isLoading = true;

        this._appConfig = this._currentApplicationService.getApplicationConfiguration();
        this._mustUpdate = this._$state.params['updateDatabase'];

        this.initializeSignalr();
        this.initStore();

        this._$scope.$on(events.isEditMode, (_event, data) => this.isEditMode = data);

        this._$scope.$on(events.connectionPointRemove, (_event, guid) => {
            this.connectionPoints = this.connectionPoints.filter(cp => cp.model.guid !== guid);
        });

        $('#updateDatabaseModal').on('hidden.bs.modal', (_event, _reason, _closed) => {
            this.updateDatabasePercent = 0;
        });
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    private getAuthorizedStates(): string[] {
        return this._$state.get()
            .filter(s => s.name.indexOf(this.state + '.') === 0
                && !s.abstract
                && this._stateAuthorizationService.canAccess(s.name))
            .sort((a, b) => a.data.sortingOrder - b.data.sortingOrder)
            .map(s => s.name);
    }

    drop() {
        const creating = this.connectionPoints.find(c => c.model.creating);
        if (creating) {
            this.selectedItem = creating;
            return false;
        }

        const connectionPoint = {
            name: '',
            type: ConnectionPointType.Manual,
            interval: 1,
            intervalTimeUnit: TimeUnit.Hour,
            guid: Helper.getGuid(),
            customProperties: {},
            forecast: { interval: 0, intervalTimeUnit: TimeUnit.Second },
            creating: true,
            outOfSyncThreshold: 30,
            dateAlignment:  DateAlignment.AlignOnPrevious
        } as ConnectionPoint;
        const viewModel = { name: connectionPoint.name, guid: connectionPoint.guid } as ConnectionPointViewModel;
        const tuple = {
            model: connectionPoint,
            viewModel: viewModel,
            isAvailable: true
        };
        this.connectionPoints.push(tuple);
        this.selectedItem = tuple;
    }

    isError(item: IConnectionPointTuple) {
        return (item.viewModel.isAvailable === false || item.viewModel.isValid === false || item.viewModel.isUpToDate === false) && !item.model.creating && !item.viewModel.isSmtp && !this.isLoading;
    }

    getPaneClass(item: IConnectionPointTuple) {
        const retval = { 'bordered': this.selectedItem === item && this.isEditMode };
        if (item.viewModel.status) {
            retval[item.viewModel.status.panel.class] = true;
        }
        if (this.isError(item) && !item.model.ignoreErrors) {
            retval['panel-danger'] = true;
        }
        return retval;
    }

    select(item: IConnectionPointTuple, $event: angular.IAngularEvent) {
        if (this.isEditMode) {
            if (this.formController && this.formController.$dirty) {
                const msg = this._$translate.instant(this._saveErrorMessage);
                this._loggerService.logWarning(msg, true);
                $event.stopPropagation();
            } else {
                this.selectedItem = item;
            }
        } else {
            this.selectedItem = item;
        }
        if ($event.preventDefault) {
            $event.preventDefault();
        }
    }

    unselect($event) {
        if (!$event.isDefaultPrevented()) {
            if (!this.formController || !this.formController.$dirty) {
                this.selectedItem = null;
            } else {
                const msg = this._$translate.instant(this._saveErrorMessage);
                this._loggerService.logWarning(msg);
                $event.stopPropagation();
            }
        }
    }

    duplicateItem(guid: string): void {
        this.isLoading = true;
        this._storeService.dispatchers().connectionPoints.duplicate(guid);
    }

    deleteItem(item: IConnectionPointTuple) {
        // TODO implement test
        if (item.viewModel.numberOfTags > 0 || item.viewModel.isDefault) {
            this._loggerService.logWarning('This connection point is in use and cannot be deleted.');
        } else {
            this._storeService.dispatchers().connectionPoints
                .deleteOne(item.model.guid)
                .then((result) => {
                    if (result < 0) {
                        this._loggerService.logError('Could not delete connection point.');
                    } else {
                        this.selectedItem = null;
                        this._loggerService.logSuccess('Connection point deleted.');
                    }
                });
        }
    }

    updateDatabase(guid: string) {
        this.isUpdatingDatabase = true;
        this.isUpdateComplete = false;

        this._signalrService.disconnect();
        this._signalrService.connect(() => {
            this._wideDatabaseUpdateService.update(guid)
                .then(() => {
                    const tuple = this.connectionPoints.find(cp => cp.model.guid === guid);
                    if (tuple) {
                        tuple.viewModel.isUpToDate = true;
                    }
                    this.isUpdateComplete = true;
                })
                .finally(() => {
                    this.isUpdatingDatabase = false;
                });
        });
    }

    sendTestMail(item: IConnectionPointTuple) {
        item.viewModel.isSmtpSending = true;
        this._mailingJobService
            .test({
                messageTitle: 'WiDE e-mail test',
                messageContent: 'Congratulations, it works.',
                connectionPointGuid: item.model.guid,
            } as MailingJobDefinition)
            .then(data => {
                if (!!data) {
                    this._loggerService.logSuccess('E-mail successfully sent. Check your mailbox!');
                } else {
                    this._loggerService.logError('Failed to send e-mail.');
                }
            })
            .finally(() => {
                item.viewModel.isSmtpSending = false;
            });
    }

    private initStore(): void {
        this._firstLoad = true;
        this.statusLoading = true;
        this._unsubscribe = this._storeService.registerAsync(
            {
                callback: store => this.storeLoader(store),
                elements: [
                    StoreElement.ConnectionPoints,
                    StoreElement.HistorianStorageConfigurations,
                    StoreElement.Tags,
                    StoreElement.AlarmEmails,
                    StoreElement.MailingJobs
                ]
            }, {
            callback: store => this.asyncStoreLoader(store),
            elements: [StoreElement.ConnectionPointsStatuses]
        }
        );
    }

    private asyncStoreLoader(store: Store) {
        this.statusLoading = store.connectionPointsStatuses.isLoading;
        this.status = store.connectionPointsStatuses.dictionary;
    }

    private storeLoader(store: Store) {
        const cpVMList = this._connectionPointMappingService.computeViewModels(
            _.values(store.connectionPoints.dictionary),
            this._appConfig.defaultDatabaseGuid,
            _.values(store.tags.dictionary),
            _.values(store.mailingJobs.dictionary),
            _.values(store.alarmEmails.dictionary),
            _.values(store.historianStorageConfigurations.dictionary)
        );

        if (this._firstLoad && this._mustUpdate && store.connectionPoints.isLoaded) {
            const defaultCP = cpVMList.find(cp => cp.viewModel.isDefault);
            if (defaultCP) {
                this._$timeout(() => {
                    this.selectedItem = defaultCP;
                    $('#updateDatabaseModal').modal('show');
                });
            }
        }

        if (this.selectedItem && cpVMList) {
            const found = cpVMList.find(cp => cp.model.guid === this.selectedItem.model.guid);
            if (found) {
                this.selectedItem = found;
            }
        }

        this._firstLoad = false;
        this.isLoading = false;
        this.connectionPoints = cpVMList;
    }

    private initializeSignalr(): void {
        const self = this;

        const progressHubProxy = this._signalrService.connection
            .createHubProxy('progressHub');

        progressHubProxy.on('updateProgressBarUpdateDatabase', (percent: number) => {
            self.updateDatabasePercent = percent;
            self._$scope.$digest();
        });

        this._signalrService.connect();
    }

    isUpdateComplete: boolean;
}

export const wtConnectionPointsTiles: ComponentDefinition = {
    componentId: 'wtConnectionPointsTiles',
    controller: ConnectionPointsTilesController,
    controllerAs: 'vm',
    template: require('./connectionPointsTiles.component.html'),
    bindings: {
        selectedItem: '=',
        formController: '=?',
        state: '@'
    }
};